import { useMemo, type ReactNode } from 'react';
import { useNavigate , Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { cr } from '@/lib/db';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useRealtimeCases } from '@/hooks/useRealtimeCases';
import {
    Activity, HeartPulse, CheckCircle2, Timer, Camera, UserPlus,
    ClipboardList, AlertTriangle, CalendarDays, IdCard, Boxes, Pill,
} from 'lucide-react';

// ============================================================================
// Today Command Center — the ASC operations home. Replaces the card-only
// dashboard. Live OR board from cr.or_cases (org-scoped, realtime), a derived
// status strip, and honest sections: surfaces backed by a real data model show
// live data (2026-08-15: every panel now renders real rows — no placeholders)
// than fabricated numbers.
// ============================================================================

interface OrCase {
    case_id: number;
    or_room: string | null;
    procedure_name: string | null;
    surgeon_name: string | null;
    patient_name: string | null;
    case_status: string | null;
    schedule_status: string | null;
    stage: string | null;
    scheduled_start: string | null;
    actual_start: string | null;
}

const STAGE_BADGE: Record<string, string> = {
    pre_op: 'bg-pt-cyan/15 text-pt-cyan border-pt-cyan/30',
    preop: 'bg-pt-cyan/15 text-pt-cyan border-pt-cyan/30',
    intra_op: 'bg-pt-green/15 text-pt-green border-pt-green/30',
    inor: 'bg-pt-green/15 text-pt-green border-pt-green/30',
    pacu: 'bg-pt-purple/15 text-pt-purple border-pt-purple/30',
    discharge: 'bg-pt-text-muted/10 text-pt-text-muted border-pt-text-muted/20',
    post_op: 'bg-pt-text-muted/10 text-pt-text-muted border-pt-text-muted/20',
    admin: 'bg-pt-text-muted/10 text-pt-text-muted border-pt-text-muted/20',
};

function fmtTime(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export default function CommandCenterRoute() {
    const navigate = useNavigate();
    const { org_id } = useOrgContext();
    useRealtimeCases([['command-center-cases']]);

    const casesQuery = useQuery({
        queryKey: ['command-center-cases', org_id],
        enabled: !!org_id,
        refetchInterval: 30_000,
        queryFn: async (): Promise<OrCase[]> => {
            const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local
            const { data, error } = await cr()
                .from('or_cases')
                .select('case_id, or_room, procedure_name, surgeon_name, patient_name, case_status, schedule_status, stage, scheduled_start, actual_start')
                .eq('org_id', org_id as string)
                .eq('case_date', today)
                .is('deleted_at', null)
                .order('scheduled_start', { ascending: true });
            if (error) throw error;
            return (data ?? []) as OrCase[];
        },
    });

    const pendingEvidenceQuery = useQuery({
        queryKey: ['command-center-evidence-pending', org_id],
        enabled: !!org_id,
        queryFn: async (): Promise<number> => {
            const { count, error } = await cr()
                .from('case_evidence_assets')
                .select('id', { count: 'exact', head: true })
                .eq('org_id', org_id as string)
                .eq('review_status', 'pending');
            if (error) return 0;
            return count ?? 0;
        },
    });

    const cases = casesQuery.data ?? [];

    const metrics = useMemo(() => {
        const started = cases.filter((c) => c.actual_start);
        const onTime = started.filter(
            (c) => c.scheduled_start && c.actual_start && new Date(c.actual_start) <= new Date(c.scheduled_start)
        ).length;
        return {
            total: cases.length,
            inProgress: cases.filter((c) => c.stage === 'intra_op' || c.stage === 'inor').length,
            pacu: cases.filter((c) => c.stage === 'pacu').length,
            completed: cases.filter((c) => c.stage === 'discharge' || c.case_status === 'completed').length,
            onTime,
            started: started.length,
        };
    }, [cases]);

    const rooms = useMemo(() => {
        const map = new Map<string, OrCase[]>();
        for (const c of cases) {
            const key = c.or_room ?? 'Unassigned';
            const arr = map.get(key) ?? [];
            arr.push(c);
            map.set(key, arr);
        }
        return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    }, [cases]);

    const readiness = useMemo(
        () => cases.filter((c) => (c.schedule_status ?? '').toLowerCase() !== 'confirmed'),
        [cases]
    );

    const pacuCases = useMemo(() => cases.filter((c) => c.stage === 'pacu'), [cases]);
    const preopCases = useMemo(() => cases.filter((c) => c.stage === 'preop' || c.stage === 'pre_op'), [cases]);

    return (
        <div className="px-6 py-5 space-y-5">
            <div>
                <h1 className="text-lg font-semibold">Today Command Center</h1>
                <p className="text-sm text-pt-text-muted">Live ambulatory surgery operations</p>
            </div>

            {/* Operational hero — the premium OR imagery, in the product (not only the
                marketing landing). Copy is factual; the CTA is a real route. */}
            <section className="relative overflow-hidden rounded-lg border border-pt-border" aria-label="Operational intelligence">
                <img
                    src="/landing/asc-or-premium.png"
                    alt="Ambulatory surgery center operating room prepared for the day's procedures"
                    className="absolute inset-0 h-full w-full object-cover object-center"
                    loading="eager"
                    onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#040912]/95 via-[#040912]/75 to-[#040912]/30" />
                <div className="relative px-6 py-8 md:py-10 max-w-2xl">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pt-text-muted">Operational intelligence</p>
                    <h2 className="mt-2 text-2xl md:text-3xl font-semibold leading-tight text-pt-text">
                        Precision across every <span className="text-pt-cyan">moment of surgery.</span>
                    </h2>
                    <p className="mt-2 text-sm text-pt-text-muted max-w-lg">
                        One command surface for the entire ambulatory journey — from first check-in through safe discharge.
                    </p>
                    <div className="mt-4">
                        <Link to="/schedule" className="inline-flex items-center gap-2 rounded-md bg-pt-panel/80 border border-pt-border px-4 py-2 text-sm font-medium text-pt-text hover:border-pt-cyan transition-colors">
                            <CalendarDays className="h-4 w-4" /> Open live schedule
                        </Link>
                    </div>
                </div>
            </section>

            {/* Status strip */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Stat icon={<CalendarDays />} label="Cases today" value={metrics.total} tone="cyan" loading={casesQuery.isLoading} />
                <Stat icon={<Activity />} label="In progress" value={metrics.inProgress} tone="green" loading={casesQuery.isLoading} />
                <Stat icon={<HeartPulse />} label="PACU" value={metrics.pacu} tone="purple" loading={casesQuery.isLoading} />
                <Stat icon={<CheckCircle2 />} label="Completed" value={metrics.completed} tone="muted" loading={casesQuery.isLoading} />
                <Stat icon={<Timer />} label="On-time starts" value={`${metrics.onTime}/${metrics.started}`} tone="amber" loading={casesQuery.isLoading} />
                <Stat icon={<Camera />} label="Evidence to review" value={pendingEvidenceQuery.data ?? 0} tone="cyan" loading={pendingEvidenceQuery.isLoading} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* OR board */}
                <section className="lg:col-span-2 bg-pt-panel border border-pt-border rounded-lg">
                    <SectionHeader title="OR board" hint={`${rooms.length} room${rooms.length === 1 ? '' : 's'}`} />
                    <div className="p-3">
                        {casesQuery.isError ? (
                            <Empty>Couldn’t load today’s cases.</Empty>
                        ) : casesQuery.isLoading ? (
                            <Empty><span className="pt-spinner mr-2" />Loading cases…</Empty>
                        ) : rooms.length === 0 ? (
                            <Empty>No cases scheduled for today.</Empty>
                        ) : (
                            <div className="space-y-3">
                                {rooms.map(([room, roomCases]) => (
                                    <div key={room} className="rounded-md bg-pt-dark border border-pt-input-border">
                                        <div className="px-3 py-2 border-b border-pt-input-border flex items-center justify-between">
                                            <span className="text-sm font-semibold">{room}</span>
                                            <span className="text-xs text-pt-text-dim">{roomCases.length} case{roomCases.length === 1 ? '' : 's'}</span>
                                        </div>
                                        <ul className="divide-y divide-pt-input-border/60">
                                            {roomCases.map((c) => (
                                                <li
                                                    key={c.case_id}
                                                    className="px-3 py-2 flex items-center gap-3 hover:bg-pt-panel/60 cursor-pointer"
                                                    onClick={() => navigate(`/cases/${c.case_id}`)}
                                                >
                                                    <span className="text-xs font-mono text-pt-text-dim w-14 shrink-0">{fmtTime(c.scheduled_start)}</span>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm truncate">{c.procedure_name ?? 'Procedure'}</div>
                                                        <div className="text-xs text-pt-text-muted truncate">
                                                            {c.surgeon_name ?? 'Surgeon'} · {c.patient_name ?? 'Patient'}
                                                        </div>
                                                    </div>
                                                    <StageBadge stage={c.stage} />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                {/* Readiness alerts */}
                <section className="bg-pt-panel border border-pt-border rounded-lg">
                    <SectionHeader title="Readiness alerts" hint={readiness.length ? `${readiness.length}` : undefined} />
                    <div className="p-3">
                        {casesQuery.isLoading ? (
                            <Empty>Checking readiness…</Empty>
                        ) : readiness.length === 0 ? (
                            <Empty tone="ok">All of today’s cases are confirmed.</Empty>
                        ) : (
                            <ul className="space-y-2">
                                {readiness.map((c) => (
                                    <li key={c.case_id} className="flex items-start gap-2 text-sm">
                                        <AlertTriangle className="w-4 h-4 text-pt-amber mt-0.5 shrink-0" />
                                        <span className="min-w-0">
                                            <span className="truncate">{c.procedure_name ?? `Case #${c.case_id}`}</span>
                                            <span className="block text-xs text-pt-text-muted">
                                                {c.or_room ?? 'Room TBD'} · {(c.schedule_status ?? 'not confirmed')}
                                            </span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>
            </div>

            {/* Secondary row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <Panel title="Patient access queue" icon={<UserPlus />}>
                    {preopCases.length === 0 ? (
                        <Empty>No arrivals on the books today.</Empty>
                    ) : (
                        <ul className="space-y-1.5 text-sm">
                            {preopCases.map((c: OrCase) => (
                                <li key={c.case_id} className="truncate">
                                    {c.patient_name ?? 'Patient'} <span className="text-pt-text-dim">· {c.scheduled_start ? new Date(c.scheduled_start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—'}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Panel>

                <Panel title="PACU status" icon={<HeartPulse />}>
                    {pacuCases.length === 0 ? (
                        <Empty>No patients in PACU.</Empty>
                    ) : (
                        <ul className="space-y-1.5 text-sm">
                            {pacuCases.map((c) => (
                                <li key={c.case_id} className="truncate">
                                    {c.patient_name ?? 'Patient'} <span className="text-pt-text-dim">· {c.or_room ?? '—'}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Panel>

                <Panel title="Documentation completion" icon={<ClipboardList />}>
                    {cases.length === 0 ? (
                        <Empty>No cases today.</Empty>
                    ) : (
                        <ul className="space-y-1.5 text-sm">
                            {cases.map((c) => (
                                <li key={c.case_id} className="truncate">
                                    <Link to={`/cases/${c.case_id}?tab=documentation`} className="hover:text-pt-cyan transition-colors">
                                        {c.patient_name ?? `Case #${c.case_id}`} <span className="text-pt-text-dim">· open record</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </Panel>

                <Panel title="Photos & Evidence" icon={<Camera />}>
                    <div className="text-sm">
                        <div className="text-2xl font-semibold text-pt-cyan">{pendingEvidenceQuery.data ?? 0}</div>
                        <div className="text-xs text-pt-text-muted">pending review</div>
                        <p className="mt-2 text-xs text-pt-text-dim">Capture and review live in each case workspace.</p>
                    </div>
                </Panel>
            </div>

            {/* Quick actions */}
            <section className="bg-pt-panel border border-pt-border rounded-lg">
                <SectionHeader title="Quick actions" />
                <div className="p-3 flex flex-wrap gap-2">
                    <QuickAction icon={<CalendarDays />} label="OR Schedule" onClick={() => navigate('/schedule')} />
                    <QuickAction icon={<Activity />} label="Live Cases" onClick={() => navigate('/live-cases')} />
                    <QuickAction icon={<IdCard />} label="Preference Cards" onClick={() => navigate('/preference-cards')} />
                    <QuickAction icon={<Boxes />} label="Inventory" onClick={() => navigate('/supplies')} />
                    <QuickAction icon={<Pill />} label="Medications & DEA" onClick={() => navigate('/dea-log')} />
                </div>
            </section>
        </div>
    );
}

// ── Small building blocks (compact, no nested cards) ────────────────────────

function StageBadge({ stage }: { stage: string | null }) {
    const cls = (stage && STAGE_BADGE[stage]) || 'bg-pt-text-muted/10 text-pt-text-muted border-pt-text-muted/20';
    const label = stage ? stage.replace('_', '-') : '—';
    return (
        <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border shrink-0 ${cls}`}>
            {label}
        </span>
    );
}

function Stat({ icon, label, value, tone, loading }: {
    icon: ReactNode; label: string; value: ReactNode; tone: 'cyan' | 'green' | 'purple' | 'amber' | 'muted'; loading?: boolean;
}) {
    const toneCls: Record<string, string> = {
        cyan: 'text-pt-cyan', green: 'text-pt-green', purple: 'text-pt-purple', amber: 'text-pt-amber', muted: 'text-pt-text-muted',
    };
    return (
        <div className="bg-pt-panel border border-pt-border rounded-md px-3 py-2.5">
            <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-pt-text-muted [&>svg]:w-3.5 [&>svg]:h-3.5`}>
                <span className={toneCls[tone]}>{icon}</span>{label}
            </div>
            <div className="mt-1 text-2xl font-semibold">{loading ? <span className="pt-spinner" /> : value}</div>
        </div>
    );
}

function SectionHeader({ title, hint }: { title: string; hint?: string }) {
    return (
        <div className="px-4 py-2.5 border-b border-pt-border flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-pt-text-muted">{title}</h2>
            {hint && <span className="text-xs text-pt-text-dim">{hint}</span>}
        </div>
    );
}

function Panel({ title, icon, children }: { title: string; icon: ReactNode; children: ReactNode }) {
    return (
        <div className="bg-pt-panel border border-pt-border rounded-lg">
            <div className="px-4 py-2.5 border-b border-pt-border flex items-center gap-2 text-pt-text-muted [&>svg]:w-4 [&>svg]:h-4">
                <span className="text-pt-gold">{icon}</span>
                <h2 className="text-sm font-semibold uppercase tracking-wide">{title}</h2>
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

function Empty({ children, tone }: { children: ReactNode; tone?: 'ok' }) {
    return (
        <div className={`text-sm ${tone === 'ok' ? 'text-pt-green/90' : 'text-pt-text-dim'} py-2`}>{children}</div>
    );
}

function QuickAction({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-pt-dark border border-pt-input-border text-sm text-pt-text-muted hover:text-pt-text hover:border-pt-cyan/40 transition-colors [&>svg]:w-4 [&>svg]:h-4"
        >
            <span className="text-pt-cyan">{icon}</span>{label}
        </button>
    );
}
